import React, { useRef, useState } from "react";
import axios from "axios";
import { Mic, Loader2, Square } from "lucide-react";
import { toast } from "sonner";

/** Botón de micrófono: graba, transcribe con Whisper y devuelve el texto. */
export const VoiceButton = ({ url, headers = {}, onText, testId = "voice-btn",
                              className = "", title = "Hablar" }) => {
  const [state, setState] = useState("idle");
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const send = async (blob) => {
    setState("sending");
    try {
      const fd = new FormData();
      fd.append("file", blob, "voz.webm");
      const { data } = await axios.post(url, fd, { headers, withCredentials: true });
      if (data.text) onText(data.text);
      else toast.error("No pude entender el audio.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pude transcribir el audio");
    } finally { setState("idle"); }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const opts = (window.MediaRecorder && MediaRecorder.isTypeSupported("audio/webm"))
        ? { mimeType: "audio/webm" } : undefined;
      const mr = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1200) {
          setState("idle");
          toast.error("No escuché nada. Mantén el botón un momento y habla claro.");
          return;
        }
        await send(blob);
      };
      mr.start();
      recRef.current = mr;
      setState("rec");
      toast.success("Grabando… habla y pulsa otra vez para enviar.");
    } catch (e) {
      toast.error("No pude acceder al micrófono. Revisa los permisos del navegador.");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (e) { console.warn('La grabadora ya estaba detenida:', e); }
    recRef.current = null;
  };

  return (
    <button type="button" data-testid={testId} title={title}
      onClick={() => (state === "rec" ? stop() : state === "idle" && start())}
      className={`flex items-center justify-center rounded-lg border transition-colors ${
        state === "rec"
          ? "border-rose-500 bg-rose-500/20 text-rose-300 animate-pulse"
          : "border-[#334155] text-slate-300 hover:bg-[#131A2A]"} ${className}`}>
      {state === "sending" ? <Loader2 className="w-5 h-5 animate-spin" />
        : state === "rec" ? <Square className="w-4 h-4" />
        : <Mic className="w-5 h-5" />}
    </button>
  );
};

export default VoiceButton;
